import React, { memo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import type { LocalizedLabel } from '@segmently/schema-models/v2';
import { mapOptionsListStyleToCSS } from '../../../../utils/styleMapper';
import { computeContainerStyle, computeCheckboxContainerStyle } from '../../../../utils/containerStyles';
import { Image } from '../../Image';
import { CheckBox } from '../../CheckBox';
import { OptionsItemText } from '../OptionsItemText';
import type { OptionsItemVariantProps } from './types';
import { getOptionItemA11yProps } from '../../../../utils/optionItemA11y';
import styles from '../OptionsItem.module.css';
import { GRID_ITEM_LAYOUT_PRESETS, toContainerDefaults } from '../../../../../reactFlow/config/gridItemLayoutPresets';

/** Strip appearance.insets from label to prevent hidden padding not controlled by UI */
function stripAppearanceInsets(label: LocalizedLabel | undefined): LocalizedLabel | undefined {
  if (!label?.appearance || !(label.appearance as Record<string, unknown>).insets) return label;
  const { insets: _, ...restAppearance } = label.appearance as Record<string, unknown>;
  return { ...label, appearance: restAppearance as LocalizedLabel['appearance'] };
}

/* ─── Derive render defaults from unified presets (fallbacks for old data) ─── */

function getContainerDefaults(layoutKey: string) {
  const preset = GRID_ITEM_LAYOUT_PRESETS[layoutKey] ?? GRID_ITEM_LAYOUT_PRESETS.BigImageTitle;
  return {
    image: preset.imageContainer ? toContainerDefaults(preset.imageContainer) : { height: 70, vAlign: 'top' },
    text: preset.textContainer ? toContainerDefaults(preset.textContainer) : null,
  };
}

export const GridCard = memo(function GridCard({
  item,
  index,
  locale,
  variables,
  selectable,
  selected,
  disabled,
  selectionMode,
  appearance,
  selectedAppearance,
  onSelect,
  className,
  style,
  'data-testid': testId,
  editPath,
  previewMode,
  cellHeightFromCellWidth,
  itemLayout,
}: OptionsItemVariantProps): React.ReactElement {
  const handleClick = useCallback(() => {
    if (selectable && !disabled && onSelect) {
      onSelect(item, index, !selected);
    }
  }, [selectable, disabled, onSelect, item, index, selected]);

  const defaults = getContainerDefaults(itemLayout ?? 'BigImageTitle');

  const activeStyle = selected ? selectedAppearance : appearance;
  const itemStyle: CSSProperties = {
    ...mapOptionsListStyleToCSS(activeStyle),
    ...(cellHeightFromCellWidth != null && { aspectRatio: `100 / ${cellHeightFromCellWidth}` }),
  };

  // Container settings from data, with preset-based fallbacks for old screens
  const appRecord = appearance as Record<string, unknown> | undefined;
  const imgContainer = appRecord?.imageContainer as Parameters<typeof computeContainerStyle>[0];
  const txtContainer = appRecord?.textContainer as Parameters<typeof computeContainerStyle>[0];
  const cbxContainer = appRecord?.checkboxContainer as Parameters<typeof computeCheckboxContainerStyle>[0];

  const imageContainerStyle = computeContainerStyle(imgContainer, defaults.image);

  const hasText = defaults.text != null || txtContainer != null;
  const textContainerStyle: CSSProperties | null = hasText
    ? {
        ...computeContainerStyle(txtContainer, defaults.text ?? { height: 30, vAlign: 'bottom' }),
        zIndex: 1,
      }
    : null;

  const checkboxContainerStyle = computeCheckboxContainerStyle(cbxContainer);

  // All padding comes from data (appearance.insets via mapOptionsListStyleToCSS)
  // .noPadding zeroes out the CSS class default padding
  const cardClass = [
    styles.card,
    styles.noPadding,
    selected ? styles.selected : '',
    disabled ? styles.disabled : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cardClass}
      style={style ? { ...itemStyle, ...style } : itemStyle}
      onClick={selectable && !disabled ? handleClick : undefined}
      {...getOptionItemA11yProps({ selectable, selected, disabled, selectionMode, onActivate: handleClick })}
      data-testid={testId}
      data-edit-path={editPath}
    >
      <div className={styles.cardContentFrame}>
        <div style={imageContainerStyle}>
          <Image
            image={item.image}
            locale={locale}
            fillContainer={typeof item.image?.appearance?.dimensions?.width !== 'number' && typeof item.image?.appearance?.dimensions?.height !== 'number'}
            previewMode={previewMode}
            editPath={editPath ? `${editPath}.image` : undefined}
          />
        </div>
        {textContainerStyle && (
          <div style={textContainerStyle}>
            <OptionsItemText
              label={stripAppearanceInsets(item.title)}
              locale={locale}
              variables={variables}
              editPath={editPath ? `${editPath}.title` : undefined}
            />
          </div>
        )}
        {checkboxContainerStyle && item.checkbox && (
          <div style={checkboxContainerStyle}>
            <CheckBox toggleMark={item.checkbox} />
          </div>
        )}
      </div>
    </div>
  );
});

GridCard.displayName = 'GridCard';
